import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RevokeOrderService } from './revokeOrder.service';
import { RevokeOrderFormControl, RevokeOrderFormData } from './revokeOrder.facade';
import { IOSHelperService } from '../../services/IOSHelper.service';
import { CoreLanguageService } from '../../services/language.service';
import { NavigationRoute } from '../../shared/enums/navigation-route.enum';
import { ValidatorsConfig } from '../../shared/validator/validator.function';
import { InputValidationMaxLength } from '../../shared/contentTypes';
import { OverlaySpinnerService } from '../overlayspinner/overlaySpiner.service';
import { RouterHelperService } from '../../services/routerHelper.service';

@Component({
  selector: 'ya-revoke-order',
  templateUrl: './revokeOrder.component.html',
  styleUrls: ['./revokeOrder.component.scss'],
  providers: [RevokeOrderService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('shake', [
      transition('true <=> false', animate('1000ms ease-in',
        keyframes([
          style({ transform: 'translate3d(-1px, 0, 0)', offset: 0.1 }),
          style({ transform: 'translate3d(2px, 0, 0)', offset: 0.2 }),
          style({ transform: 'translate3d(-4px, 0, 0)', offset: 0.3 }),
          style({ transform: 'translate3d(4px, 0, 0)', offset: 0.4 }),
          style({ transform: 'translate3d(-4px, 0, 0)', offset: 0.5 }),
          style({ transform: 'translate3d(4px, 0, 0)', offset: 0.6 }),
          style({ transform: 'translate3d(-4px, 0, 0)', offset: 0.7 }),
          style({ transform: 'translate3d(2px, 0, 0)', offset: 0.8 }),
          style({ transform: 'translate3d(-1px, 0, 0)', offset: 0.9 }),
        ])
      )),
    ]),
  ],
})
export class RevokeOrderComponent {

  /** Exposed for template bindings. */
  public readonly InputValidationMaxLength = InputValidationMaxLength;
  /** Exposed for template bindings. */
  public readonly RevokeOrderFormControl = RevokeOrderFormControl;

  /** mat-tab-group selected index. 0 = Tab 1 (entry), 1 = Tab 2 (confirmation). */
  public activeTab: number = 0;

  /** True while the HTTP submit request is in flight. */
  public isSubmitting: boolean = false;

  /** Snapshot of form data taken when the user advances to Tab 2. */
  public summaryData: RevokeOrderFormData = null;

  /** State map for shake animations. Initialized to false so the first toggle is a real false→true transition. */
  public states: { tab1: boolean } = { tab1: false };

  private _focused: { [key: string]: boolean } = {};

  /** Reactive form group. */
  public form: FormGroup;

  constructor(
    private _revokeOrderService: RevokeOrderService,
    private _overlaySpinnerService: OverlaySpinnerService,
    private _iosHelperService: IOSHelperService,
    private _coreLanguageService: CoreLanguageService,
    private _router: Router,
    private _location: Location,
    private _cdr: ChangeDetectorRef,
    private _routerHelperService: RouterHelperService,
  ) {
    const me = this;
    me.form = new FormGroup({
      [RevokeOrderFormControl.Name]: new FormControl('', [Validators.required, Validators.maxLength(InputValidationMaxLength.name)]),
      [RevokeOrderFormControl.Email]: new FormControl('', ValidatorsConfig.forEmail()),
      [RevokeOrderFormControl.OrderNumber]: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      [RevokeOrderFormControl.Articles]: new FormControl('', [Validators.maxLength(200)]),
      [RevokeOrderFormControl.Reason]: new FormControl('', [Validators.maxLength(200)]),
    });
  }

  /** Fixes iOS WebView scroll after keyboard hide. Call on (blur) of any input or textarea. */
  public setWebViewAfterBlur(): void {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  /** Tracks focus per control to drive the Angular-based floating-label state (so the label position follows the real data/focus state, not CSS :placeholder-shown). */
  public onInputFocus(control: RevokeOrderFormControl): void {
    const me = this;
    me._focused[control] = true;
    me._cdr.markForCheck();
  }

  /** Clears the focus flag; the label returns into the input when the field is empty (isFloated falls back to the FormControl value). */
  public onInputBlur(control: RevokeOrderFormControl): void {
    const me = this;
    me._focused[control] = false;
    me._cdr.markForCheck();
  }

  /** Returns true when the label should be in the floated (above) position. */
  public isFloated(control: RevokeOrderFormControl): boolean {
    const me = this;
    return !!me._focused[control] || !!me.form.get(control).value;
  }

  /** Navigates back: from Tab 1 goes to previous page; from Tab 2 returns to Tab 1. */
  public navigateBack(): void {
    const me = this;
    if (me.activeTab === 0) {
      me._location.back();
    } else {
      me.activeTab = 0;
      me._cdr.markForCheck();
    }
  }

  private readonly _requiredControls = new Set<RevokeOrderFormControl>([
    RevokeOrderFormControl.Name,
    RevokeOrderFormControl.Email,
    RevokeOrderFormControl.OrderNumber,
  ]);

  private readonly _langKeyMap: Partial<Record<RevokeOrderFormControl, string>> = {
    [RevokeOrderFormControl.Name]: 'phRevokeOrder_FieldName',
    [RevokeOrderFormControl.Email]: 'phRevokeOrder_FieldEmail',
    [RevokeOrderFormControl.OrderNumber]: 'phRevokeOrder_FieldOrderNumber',
    [RevokeOrderFormControl.Articles]: 'phRevokeOrder_FieldArticles',
    [RevokeOrderFormControl.Reason]: 'phRevokeOrder_FieldReason',
  };

  /** Resolves the display label for a form field. */
  public getFieldLabel(control: RevokeOrderFormControl): string {
    const me = this;
    return me._coreLanguageService.value(me._langKeyMap[control]);
  }

  /** Returns true when the given control is required (Name, Email, OrderNumber). */
  public isRequired(control: RevokeOrderFormControl): boolean {
    const me = this;
    return me._requiredControls.has(control);
  }

  /** Toggles the shake animation on the Tab-1 header label to signal incomplete fields. */
  public shakeTab1(): void {
    const me = this;
    me.states['tab1'] = !me.states['tab1'];
  }

  /**
   * Advances to Tab 2 after marking required fields touched and validating.
   * When form is invalid the tab switch is blocked and shakeTab1() fires on the Tab-1 header label.
   * Tab 2 is only reachable when form.valid is true.
   */
  public goToConfirmation(): void {
    const me = this;
    me.form.get(RevokeOrderFormControl.Name).markAsTouched();
    me.form.get(RevokeOrderFormControl.Email).markAsTouched();
    me.form.get(RevokeOrderFormControl.OrderNumber).markAsTouched();
    if (!me.form.valid) {
      me.shakeTab1();
      me._cdr.markForCheck();
      return;
    }
    me.summaryData = me._buildFormData();
    me.activeTab = 1;
    me._cdr.markForCheck();
  }

  /** Submits the revoke declaration; navigates to the result route on completion. */
  public confirmRevoke(): void {
    const me = this;
    if (me.isSubmitting) {
      return;
    }
    me.isSubmitting = true;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._revokeOrderService.submitRevokeOrder(me.summaryData)
      .then((success) => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._routerHelperService.setRouteTargetDataByKey('revokeOutcome', success ? 'success' : 'error');
        me._router.navigate([NavigationRoute.RevokeOrderResult], { replaceUrl: true });
      })
      .catch(() => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._routerHelperService.setRouteTargetDataByKey('revokeOutcome', 'error');
        me._router.navigate([NavigationRoute.RevokeOrderResult], { replaceUrl: true });
      });
  }

  /** Builds a typed form data snapshot from the current form values. */
  private _buildFormData(): RevokeOrderFormData {
    const me = this;
    const formValue = me.form.value;
    return {
      name: (formValue[RevokeOrderFormControl.Name] || '').trim(),
      email: (formValue[RevokeOrderFormControl.Email] || '').trim(),
      orderNumber: (formValue[RevokeOrderFormControl.OrderNumber] || '').trim(),
      articles: formValue[RevokeOrderFormControl.Articles] ? formValue[RevokeOrderFormControl.Articles].trim() || null : null,
      reason: formValue[RevokeOrderFormControl.Reason] ? formValue[RevokeOrderFormControl.Reason].trim() || null : null,
    };
  }
}
